import { Link } from "react-router-dom";
import { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  FaEnvelope, FaMagic, FaGoogle, FaPaperPlane, FaCheckCircle,
  FaArrowRight, FaStar, FaBolt
} from "react-icons/fa";

// ── Santharion Star Logo ─────────────────────────────────────────
function SantharionLogo({ size = 48 }) {
  const s = size;
  const id = `sg-${s}`;
  return (
    <svg width={s} height={s} viewBox="0 0 100 100" fill="none">
      <defs>
        <radialGradient id={`${id}-bg`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#2d1b4e"/>
          <stop offset="100%" stopColor="#0f0818"/>
        </radialGradient>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f97316"/>
          <stop offset="50%" stopColor="#ec4899"/>
          <stop offset="100%" stopColor="#8b5cf6"/>
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill={`url(#${id}-bg)`} stroke={`url(#${id})`} strokeWidth="2"/>
      <polygon points="50,18 58,40 82,40 63,55 70,78 50,63 30,78 37,55 18,40 42,40"
        fill={`url(#${id})`} opacity="0.95"/>
    </svg>
  );
}

// ── Floating orbs background ─────────────────────────────────────
function Orbs() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[
        { w:600,h:600,t:"-10%",l:"-15%",  c:"rgba(249,115,22,0.12)" },
        { w:500,h:500,t:"20%", r:"-10%",  c:"rgba(236,72,153,0.1)"  },
        { w:700,h:700,t:"55%", l:"10%",   c:"rgba(139,92,246,0.08)" },
        { w:400,h:400,t:"70%", r:"5%",    c:"rgba(249,115,22,0.08)" },
      ].map((o,i) => (
        <motion.div key={i}
          animate={{ scale:[1,1.12,1], opacity:[0.7,1,0.7] }}
          transition={{ duration: 6+i*2, repeat:Infinity, ease:"easeInOut", delay:i*1.5 }}
          style={{
            position:"absolute", width:o.w, height:o.h,
            top:o.t, left:o.l, right:o.r,
            borderRadius:"50%",
            background:`radial-gradient(circle, ${o.c}, transparent 70%)`,
            filter:"blur(40px)",
          }}
        />
      ))}
    </div>
  );
}

// ── Animated email SVG mockup ─────────────────────────────────────
function EmailMockup() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep(s => (s+1) % 4), 2000);
    return () => clearInterval(t);
  }, []);

  const steps = [
    { label: "You type:", text: "Send a professional email to my client about project update", color:"#fb923c" },
    { label: "AI picks tone:", text: "Generating 3 templates for you...", color:"#ec4899" },
    { label: "You choose:", text: "✅ Professional template selected", color:"#8b5cf6" },
    { label: "Sent! 🎉",   text: "Email delivered from your Gmail", color:"#4ade80" },
  ];

  return (
    <div className="relative w-full max-w-lg mx-auto">
      {/* Email window frame */}
      <div className="rounded-3xl overflow-hidden shadow-2xl"
        style={{ background:"rgba(15,8,30,0.9)", border:"1px solid rgba(249,115,22,0.25)" }}>
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-5 py-3.5"
          style={{ background:"rgba(249,115,22,0.06)", borderBottom:"1px solid rgba(249,115,22,0.15)" }}>
          <div className="w-3 h-3 rounded-full bg-red-500 opacity-80"/>
          <div className="w-3 h-3 rounded-full bg-yellow-500 opacity-80"/>
          <div className="w-3 h-3 rounded-full bg-green-500 opacity-80"/>
          <span className="ml-3 text-xs font-medium" style={{ color:"#9ca3af" }}>Santharion AI — Email Automation</span>
        </div>
        {/* Chat area */}
        <div className="p-6 space-y-4 min-h-[280px]">
          {steps.slice(0, step+1).map((s, i) => (
            <motion.div key={i} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
              className="flex gap-3 items-start">
              <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background:`${s.color}22`, border:`1px solid ${s.color}44` }}>
                <FaEnvelope size={12} style={{ color:s.color }}/>
              </div>
              <div>
                <p className="text-xs mb-1" style={{ color:"#6b7280" }}>{s.label}</p>
                <p className="text-sm font-medium" style={{ color:"#f3f4f6" }}>{s.text}</p>
              </div>
            </motion.div>
          ))}
          {/* Cursor blink */}
          <motion.div animate={{ opacity:[1,0,1] }} transition={{ repeat:Infinity, duration:1 }}
            className="w-2 h-4 rounded-sm inline-block ml-1"
            style={{ background:"#f97316" }}/>
        </div>
        {/* 3 Template cards mini */}
        <div className="px-6 pb-6 grid grid-cols-3 gap-2">
          {["💼 Pro","😊 Friendly","⚡ Brief"].map((t,i) => (
            <motion.div key={i}
              animate={{ scale: step === 2 && i===0 ? [1,1.05,1] : 1 }}
              transition={{ repeat: step===2?3:0, duration:0.4 }}
              className="rounded-xl py-2 text-center text-xs font-semibold cursor-pointer"
              style={{
                background: step===2&&i===0 ? "rgba(139,92,246,0.3)" : "rgba(255,255,255,0.05)",
                border: step===2&&i===0 ? "1px solid #8b5cf6" : "1px solid rgba(255,255,255,0.08)",
                color: step===2&&i===0 ? "#c084fc" : "#9ca3af",
              }}>
              {t}
            </motion.div>
          ))}
        </div>
      </div>
      {/* Floating badges */}
      <motion.div animate={{ y:[0,-8,0] }} transition={{ repeat:Infinity, duration:3 }}
        className="absolute -top-4 -right-4 px-3 py-1.5 rounded-full text-xs font-bold shadow-lg"
        style={{ background:"linear-gradient(135deg,#f97316,#ec4899)", color:"white" }}>
        ✨ AI-Powered
      </motion.div>
      <motion.div animate={{ y:[0,8,0] }} transition={{ repeat:Infinity, duration:3.5 }}
        className="absolute -bottom-4 -left-4 px-3 py-1.5 rounded-full text-xs font-bold shadow-lg"
        style={{ background:"rgba(139,92,246,0.9)", color:"white" }}>
        📬 Gmail Connected
      </motion.div>
    </div>
  );
}

// ── Main Home ────────────────────────────────────────────────────
function Home() {


  // Track visitor on page load
  useEffect(() => {
    const isNewSession = !sessionStorage.getItem("visited");
    if (isNewSession) sessionStorage.setItem("visited", "1");
    fetch("/api/visitors/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ newSession: isNewSession })
    }).catch(() => {}); // silent fail — never break UI
  }, []);
  const heroRef = useRef(null);

  const features = [
    { icon:"📝", title:"Tell AI what to send", desc:"Just describe your email in plain words — AI handles the rest." },
    { icon:"🎨", title:"Pick from 3 tone templates", desc:"Professional, Friendly, or Brief — you always stay in control." },
    { icon:"📬", title:"Sends from your real Gmail", desc:"No fake addresses. Emails arrive from your own Google account." },
    { icon:"🔒", title:"Zero password stored", desc:"OAuth only. Your credentials never touch our servers." },
    { icon:"⚡", title:"Ready in seconds", desc:"Sign in with Google, describe your email, done. No setup needed." },
    { icon:"♾️", title:"Completely free", desc:"No trials, no limits, no credit card. Always free." },
  ];

  const steps = [
    { n:"01", icon:<FaGoogle/>,      title:"Sign in with Google",       desc:"One click. No passwords stored. OAuth secured." },
    { n:"02", icon:<FaEnvelope/>,    title:"Tell AI your email intent",  desc:"\"Email my client about the project delay\"" },
    { n:"03", icon:<FaMagic/>,       title:"AI generates 3 templates",   desc:"Choose Professional, Friendly, or Brief tone." },
    { n:"04", icon:<FaPaperPlane/>,  title:"Edit & send in one click",   desc:"Sent from your Gmail. Real, professional emails." },
  ];

  const testimonials = [
    { name:"Priya S.", role:"Freelancer", text:"I send 10+ client emails daily. Santharion cuts that to 2 minutes. Incredible." },
    { name:"Rahul K.", role:"Startup Founder", text:"Finally an AI that actually sends the email. Not just writes it." },
    { name:"Meera T.", role:"HR Manager", text:"The 3 templates feature is genius. Always pick the right tone now." },
  ];

  return (
    <div className="relative min-h-screen text-white overflow-x-hidden"
      style={{ background:"#080410", fontFamily:"'Sora', sans-serif" }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800;900&display=swap');
        .grad-text { background: linear-gradient(135deg, #f97316, #ec4899, #8b5cf6); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .grad-border { border: 1px solid transparent; background: linear-gradient(#0f0820, #0f0820) padding-box, linear-gradient(135deg, #f97316, #ec4899, #8b5cf6) border-box; }
        .glow-btn { box-shadow: 0 0 30px rgba(249,115,22,0.35), 0 0 60px rgba(236,72,153,0.15); }
        .glow-btn:hover { box-shadow: 0 0 40px rgba(249,115,22,0.55), 0 0 80px rgba(236,72,153,0.25); }
      `}</style>

      {/* ─────────────── HERO ─────────────────────────────── */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 py-24">

        {/* ── Video background ── */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          {/* Dark base fallback */}
          <div className="absolute inset-0" style={{ background:"#080410" }}/>

          {/* The actual video — place bg-video.mp4 in /public folder */}
          <video
            autoPlay muted loop playsInline
            style={{
              position:"absolute", top:"50%", left:"50%",
              transform:"translate(-50%,-50%)",
              minWidth:"100%", minHeight:"100%",
              width:"auto", height:"auto",
              objectFit:"cover",
              opacity:0.35,
              filter:"saturate(1.6) hue-rotate(220deg) brightness(0.75) contrast(1.1)",
            }}>
            <source src="/bg-video.mp4" type="video/mp4"/>
          </video>

          {/* Top dark overlay — so navbar is always readable */}
          <div className="absolute inset-0" style={{
            background:"linear-gradient(180deg, rgba(8,4,16,0.75) 0%, rgba(8,4,16,0.2) 25%, rgba(8,4,16,0.1) 50%, rgba(8,4,16,0.2) 75%, rgba(8,4,16,0.92) 100%)",
          }}/>
          {/* Side vignette */}
          <div className="absolute inset-0" style={{
            background:"radial-gradient(ellipse 100% 100% at 50% 50%, transparent 40%, rgba(8,4,16,0.7) 100%)",
          }}/>
          {/* Purple color wash to match brand */}
          <div className="absolute inset-0" style={{
            background:"radial-gradient(ellipse 80% 60% at 20% 30%, rgba(249,115,22,0.08) 0%, transparent 60%), radial-gradient(ellipse 60% 70% at 80% 60%, rgba(139,92,246,0.1) 0%, transparent 60%)",
          }}/>
        </div>

        <Orbs/>

        {/* Hero content wrapper — above video */}
        <div className="relative z-10 flex flex-col items-center w-full">

        {/* Badge */}
        <motion.div initial={{ opacity:0, y:-20 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.6 }}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold mb-8 grad-border"
          style={{ color:"#fb923c" }}>
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"/>
          Email Automation · Free · Powered by AI
        </motion.div>

        {/* Logo + Name */}
        <motion.div initial={{ opacity:0, scale:0.8 }} animate={{ opacity:1, scale:1 }}
          transition={{ duration:0.7, type:"spring" }}
          className="flex flex-col items-center gap-4 mb-8">
          <motion.div animate={{ rotate:[0,5,-5,0] }} transition={{ repeat:Infinity, duration:8, ease:"easeInOut" }}>
            <SantharionLogo size={100}/>
          </motion.div>
          <h1 className="text-6xl md:text-8xl font-black tracking-tight leading-none">
            <span className="grad-text">Santharion</span>
            <span className="text-white"> AI</span>
          </h1>
          <p className="text-lg font-medium" style={{ color:"#9ca3af", letterSpacing:"0.15em" }}>
            BY LEARNHUBAI
          </p>
        </motion.div>

        {/* Headline */}
        <motion.div initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.3, duration:0.8 }}>
          <h2 className="text-3xl md:text-5xl font-bold max-w-3xl mx-auto leading-tight mb-6" style={{ color:"#f3f4f6" }}>
            Write emails in seconds.<br/>
            <span className="grad-text">Send them with one click.</span>
          </h2>
          <p className="text-lg md:text-xl max-w-2xl mx-auto mb-10" style={{ color:"#9ca3af" }}>
            Tell Santharion AI what you want to say. It generates 3 perfectly toned templates —
            Professional, Friendly, or Brief — and sends directly from your Gmail.
          </p>
        </motion.div>

        {/* CTA buttons */}
        <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.5 }}
          className="flex flex-col sm:flex-row gap-4 mb-16">
          <Link to="/ai-assistant">
            <motion.button whileHover={{ scale:1.06 }} whileTap={{ scale:0.97 }}
              className="glow-btn inline-flex items-center gap-3 px-10 py-5 rounded-2xl text-lg font-bold text-white transition-all duration-300"
              style={{ background:"linear-gradient(135deg,#f97316,#ec4899,#8b5cf6)" }}>
              <FaEnvelope/> Start Sending Emails Free <FaArrowRight className="text-sm"/>
            </motion.button>
          </Link>
          <Link to="/ai-assistant">
            <motion.button whileHover={{ scale:1.04 }} whileTap={{ scale:0.97 }}
              className="inline-flex items-center gap-3 px-10 py-5 rounded-2xl text-lg font-semibold transition-all duration-300 grad-border"
              style={{ color:"#fb923c" }}>
              <FaGoogle/> Sign in with Google
            </motion.button>
          </Link>
        </motion.div>

        {/* Trust row */}
        <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.7 }}
          className="flex flex-wrap justify-center gap-6 text-sm" style={{ color:"#6b7280" }}>
          {["✅ No credit card","✅ No password stored","✅ Sends from your Gmail","✅ Always free"].map((t,i)=>(
            <span key={i}>{t}</span>
          ))}
        </motion.div>

        </div>{/* end z-10 hero content wrapper */}

        {/* Scroll indicator */}
        <motion.div animate={{ y:[0,10,0] }} transition={{ repeat:Infinity, duration:1.8 }}
          className="absolute bottom-10 flex flex-col items-center gap-2 z-10" style={{ color:"#4b5563" }}>
          <span className="text-xs tracking-widest uppercase">Scroll</span>
          <div className="w-px h-10" style={{ background:"linear-gradient(to bottom, #f97316, transparent)" }}/>
        </motion.div>
      </section>

      {/* ─────────────── LIVE DEMO SECTION ────────────────── */}
      <section className="relative py-32 px-6" style={{ background:"rgba(15,8,30,0.6)" }}>
        <Orbs/>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Left: text */}
            <motion.div initial={{ opacity:0, x:-40 }} whileInView={{ opacity:1, x:0 }}
              viewport={{ once:true }} transition={{ duration:0.8 }}>
              <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase px-3 py-1.5 rounded-full mb-6"
                style={{ background:"rgba(249,115,22,0.1)", color:"#f97316", border:"1px solid rgba(249,115,22,0.3)" }}>
                <FaBolt/> See it in action
              </span>
              <h2 className="text-4xl md:text-5xl font-black mb-6 leading-tight">
                From idea to
                <span className="grad-text"> sent email</span>
                <br/>in under 30 seconds
              </h2>
              <p className="text-lg mb-8" style={{ color:"#9ca3af" }}>
                No more staring at blank compose windows. Just tell Santharion AI what you need —
                it writes 3 options, you pick one, it sends. That's it.
              </p>
              <div className="space-y-4">
                {[
                  "\"Follow up with my client about invoice\"",
                  "\"Apologize for the project delay professionally\"",
                  "\"Send a quick update to my team about the meeting\"",
                ].map((ex, i) => (
                  <motion.div key={i} initial={{ opacity:0, x:-20 }} whileInView={{ opacity:1, x:0 }}
                    viewport={{ once:true }} transition={{ delay:i*0.1 }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl"
                    style={{ background:"rgba(249,115,22,0.06)", border:"1px solid rgba(249,115,22,0.15)" }}>
                    <FaCheckCircle style={{ color:"#f97316", flexShrink:0 }}/>
                    <span className="text-sm italic" style={{ color:"#d1d5db" }}>{ex}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            {/* Right: animated mockup */}
            <motion.div initial={{ opacity:0, x:40 }} whileInView={{ opacity:1, x:0 }}
              viewport={{ once:true }} transition={{ duration:0.8 }}>
              <EmailMockup/>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─────────────── HOW IT WORKS ─────────────────────── */}
      <section className="relative py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }} className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              How it <span className="grad-text">works</span>
            </h2>
            <p style={{ color:"#9ca3af" }}>Four steps. Thirty seconds. Real emails.</p>
          </motion.div>

          <div className="relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-12 left-0 right-0 h-px mx-24"
              style={{ background:"linear-gradient(90deg,#f97316,#ec4899,#8b5cf6)" }}/>

            <div className="grid md:grid-cols-4 gap-8">
              {steps.map((s, i) => (
                <motion.div key={i} initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }}
                  viewport={{ once:true }} transition={{ delay:i*0.15 }}
                  className="flex flex-col items-center text-center">
                  {/* Circle */}
                  <div className="relative mb-6">
                    <motion.div whileHover={{ scale:1.1 }}
                      className="w-24 h-24 rounded-2xl flex items-center justify-center text-2xl relative z-10 grad-border"
                      style={{ background:"#0f0820" }}>
                      <span style={{ color:"#f97316" }}>{s.icon}</span>
                    </motion.div>
                    <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-black z-20"
                      style={{ background:"linear-gradient(135deg,#f97316,#ec4899)", color:"white" }}>
                      {i+1}
                    </span>
                  </div>
                  <h3 className="font-bold text-white mb-2">{s.title}</h3>
                  <p className="text-sm" style={{ color:"#6b7280" }}>{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── FEATURES GRID ────────────────────── */}
      <section className="relative py-32 px-6" style={{ background:"rgba(15,8,30,0.5)" }}>
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              Why <span className="grad-text">Santharion AI</span>?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div key={i} initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }}
                viewport={{ once:true }} transition={{ delay:i*0.1 }}
                whileHover={{ y:-6, scale:1.02 }}
                className="rounded-2xl p-6 transition-all duration-300 grad-border"
                style={{ background:"rgba(15,8,30,0.8)" }}>
                <span className="text-4xl mb-4 block">{f.icon}</span>
                <h3 className="font-bold text-white mb-2 text-lg">{f.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color:"#9ca3af" }}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── TESTIMONIALS ─────────────────────── */}
      <section className="relative py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }} className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">
              People <span className="grad-text">love it</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div key={i} initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }}
                viewport={{ once:true }} transition={{ delay:i*0.1 }}
                className="rounded-2xl p-6 grad-border" style={{ background:"rgba(15,8,30,0.8)" }}>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_,j)=><FaStar key={j} style={{ color:"#f97316" }} size={12}/>)}
                </div>
                <p className="text-sm mb-4 italic leading-relaxed" style={{ color:"#d1d5db" }}>"{t.text}"</p>
                <div>
                  <p className="font-bold text-white text-sm">{t.name}</p>
                  <p className="text-xs" style={{ color:"#6b7280" }}>{t.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── FINAL CTA ────────────────────────── */}
      <section className="relative py-32 px-6 text-center overflow-hidden">
        <Orbs/>
        <div className="relative z-10 max-w-3xl mx-auto">
          <motion.div initial={{ opacity:0, scale:0.9 }} whileInView={{ opacity:1, scale:1 }}
            viewport={{ once:true }} transition={{ duration:0.7 }}>
            <motion.div animate={{ rotate:[0,5,-5,0] }} transition={{ repeat:Infinity, duration:8 }}
              className="inline-block mb-8">
              <SantharionLogo size={80}/>
            </motion.div>
            <h2 className="text-5xl md:text-6xl font-black mb-6">
              Ready to send
              <br/><span className="grad-text">smarter emails?</span>
            </h2>
            <p className="text-lg mb-10" style={{ color:"#9ca3af" }}>
              Join thousands who've automated their email workflow with Santharion AI.
              Free forever. No setup. Just results.
            </p>
            <Link to="/ai-assistant">
              <motion.button whileHover={{ scale:1.06 }} whileTap={{ scale:0.97 }}
                className="glow-btn inline-flex items-center gap-3 px-12 py-6 rounded-2xl text-xl font-black text-white transition-all duration-300"
                style={{ background:"linear-gradient(135deg,#f97316,#ec4899,#8b5cf6)" }}>
                <FaEnvelope size={20}/> Get Started — It's Free <FaArrowRight/>
              </motion.button>
            </Link>
            <p className="mt-5 text-sm" style={{ color:"#4b5563" }}>
              No account needed · Signs in with Google · Sends from your real Gmail
            </p>
          </motion.div>
        </div>
      </section>

      {/* ─────────────── FOOTER ───────────────────────────── */}
      <footer className="py-12 px-6" style={{ borderTop:"1px solid rgba(249,115,22,0.15)" }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-start gap-10">
          <div className="flex items-center gap-3">
            <SantharionLogo size={40}/>
            <div>
              <p className="font-black text-lg">
                <span className="grad-text">Santharion AI</span>
              </p>
              <p className="text-xs" style={{ color:"#4b5563" }}>by LearnHubAI</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-10 text-sm" style={{ color:"#6b7280" }}>
            <div>
              <p className="font-semibold text-white mb-2">Founder</p>
              <p>Gurucharan P</p>
              <a href="tel:9740043604" className="hover:text-orange-400 transition">📞 9740043604</a>
              <br/>
              <a href="mailto:cguru203@gmail.com" className="hover:text-orange-400 transition">📧 cguru203@gmail.com</a>
            </div>
            <div>
              <p className="font-semibold text-white mb-2">Co-Founder</p>
              <p>Harish G</p>
              <a href="tel:9731337072" className="hover:text-orange-400 transition">📞 9731337072</a>
            </div>
          </div>
        </div>
        <div className="text-center mt-10 space-y-3">
          <div className="flex justify-center gap-6 text-xs flex-wrap">
            <Link to="/privacy" className="hover:text-orange-400 transition" style={{ color:"#6b7280" }}>
              Privacy Policy
            </Link>
            <span style={{ color:"#374151" }}>·</span>
            <Link to="/terms" className="hover:text-orange-400 transition" style={{ color:"#6b7280" }}>
              Terms of Service
            </Link>
            <span style={{ color:"#374151" }}>·</span>
            <a href="mailto:cguru203@gmail.com" className="hover:text-orange-400 transition" style={{ color:"#6b7280" }}>
              Contact Us
            </a>
          </div>
          <p className="text-xs" style={{ color:"#374151" }}>© 2026 Santharion AI · LearnHubAI · All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default Home;